import { useCallback } from "react";

import { useUpdateEffect } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useSWRConfig } from "swr";

import slice, {
  performCompletePassword,
  performSignIn,
  performSignOut,
  performVerify,
  ValidationTypes,
} from "~/context/features/auth";
import { inMemory } from "~/middleware";
import { addOnboardingSkipParam } from "~/utils/url";

import { useNavigate, useProduct, useQueryParam, useStore } from ".";
import useDispatch from "./useDispatch";

interface VerificationPayloadProps {
  validation: ValidationTypes | null;
}

function useAuth() {
  const { cache } = useSWRConfig();
  const auth = useStore(({ auth }) => auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const product = useProduct();
  const params = useQueryParam("utm_source");
  const productQuery = product.name ? `?product=${product.name.toLowerCase()}` : "";
  const utmQuery = params?.utm_source === "lead" ? "utm_source=lead" : "";

  const queryString = [productQuery, utmQuery].filter(Boolean).join("&");
  const urlProfile = `/signup/profile/${queryString ? `${queryString}` : ""}`;
  const urlValidateEmail = `/signup/validate-email/${queryString ? `${queryString}` : ""}`;
  const urlSignIn = `/signin/${productQuery ? `${productQuery}` : ""}`;
  const isUserIn = !!auth.session;

  const setAuthenticate = useCallback(
    (email: string, password: string) => {
      dispatch(slice.actions.setAuthenticate({ email, password }));
    },
    [dispatch]
  );

  const handleSignIn = useCallback(
    async (email: string, password: string, redirectTo?: string) => {
      const res = await dispatch(performSignIn({ email, password }));

      if ((res.payload as any).auth_state === "AUTHENTICATED") {
        if (redirectTo) {
          if (redirectTo.includes("trail")) return navigate("/" + redirectTo);

          return navigate(addOnboardingSkipParam(redirectTo, product.name));
        } else {
          return navigate("/");
        }
      }
    },
    [dispatch, navigate, product.name]
  );

  const completeAuthentication = async () => {
    await handleSignIn(auth.authenticate?.email as string, auth.authenticate?.password as string);

    navigate(addOnboardingSkipParam(urlProfile, product.name), { state: { canComplete: true } });
  };

  const navigateToEmail = useCallback(
    (email: string, password: string) => {
      setAuthenticate(email, password);
      navigate(urlValidateEmail, { state: { email } });
    },
    [navigate, urlValidateEmail, setAuthenticate]
  );

  const shouldNavigate = useCallback(
    (verification: ValidationTypes | null) => {
      if (verification === ValidationTypes.EMAIL) return navigateToEmail;

      return null;
    },
    [navigateToEmail]
  );

  const signIn = useCallback(
    async (email: string, password: string, redirectTo?: string) => {
      const { payload } = await dispatch(performVerify({ email }));
      const validation = (payload as VerificationPayloadProps).validation;

      const navigate = shouldNavigate(validation);

      if (navigate) navigate(email, password);
      else handleSignIn(email, password, redirectTo);
    },
    [dispatch, handleSignIn, shouldNavigate]
  );

  const signOut: Replace<typeof slice.actions.signOut, void> = useCallback(() => {
    dispatch(performSignOut());
    for (const key of inMemory.keys()) cache.delete(key);
  }, [cache, dispatch]);

  const completePassword = useCallback(
    async (password: string) => {
      const response = await dispatch(
        performCompletePassword({
          new_password: password,
          email: auth.provisional?.userAttributes.email as string,
          session_token: auth.provisional?.session_token as string,
        })
      );

      if (!("error" in response)) navigate(urlSignIn);
    },
    [auth.provisional?.session_token, auth.provisional?.userAttributes.email, dispatch, navigate, urlSignIn]
  );

  useUpdateEffect(() => {
    switch (auth.authState) {
      case "NEW_PASSWORD_REQUIRED":
        navigate(`${"/signin/provisional-password/"}${productQuery}`);
    }
  }, [auth.authState, dispatch, navigate, pathname, product.id, product.name, productQuery]);

  return {
    ...auth,
    isUserIn,
    signIn,
    signOut,
    completePassword,
    setAuthenticate,
    completeAuthentication,
    handleSignIn,
  };
}

export default useAuth;
